<template>
  <Portal
    v-if="confirmDialog.isRevealed.value"
    to="root-widget"
  >
    <ConfirmationModal
      class="delete-comment-popup"
      :title="translate('deleteMessage')"
      :confirm-button-text="translate('delete')"
      :width-class="'w-[80%] md:w-[35%]'"
      :confirm-button-type="'popup-button-wrap delete'"
      :cancel-button-type="'popup-button-wrap secondary'"
      :button-classes="''"
      :button-wrap-class="'justify-end'"
      @confirm="deleteComment"
      @cancel="hidePopUp"
    >
      <template #content>
        <div class="popup-body-message text-text-default">
          <strong>{{ translate('deleteCommentPopupMessage') }}</strong>
          {{ translate('cantUndoneAction') }}
        </div>
        <div class="comment-to-delete-preview">
          <div class="flex items-center">
            <UserAvatar
              :key="comment.user_id"
              :member="owner"
            />
            <CommentPostedByRow :comment="comment" />
          </div>
          <vue-scroll
            :ops="opsScroll"
            class="delete-comment-popup-body"
          >
            <div class="posted-user-comment">
              <CommentListItemDescriptionRow :comment="comment" />
            </div>
          </vue-scroll>
        </div>
      </template>
    </ConfirmationModal>
  </Portal>
</template>

<script setup lang="ts">
import { ref, computed, getCurrentInstance, onMounted } from 'vue';
import { Portal } from 'portal-vue';
import { useConfirmDialog } from '@vueuse/core';
import CommentPostedByRow from '@/components/widgets/CommentPostedByRow';
import CommentListItemDescriptionRow from '@/components/widgets/CommentListItemDescriptionRow';
import UserAvatar from '@/components/widgets/UserAvatar';
import EventBus from '@/utilities/eventBus';
import styleVariables from '@/assets/scss/variables.module.scss';
import ConfirmationModal from '@/components/widgets/ConfirmationModal.vue';
import { ResourceType } from '@/types/resources';
import cloneDeep from 'lodash/cloneDeep';
import useTranslate from '@/utilities/composables/useTranslate';

const opsScroll = ref({
  scrollPanel: {
    scrollingX: false,
  },
  bar: {
    background: styleVariables.gray200,
    keepShow: true,
  },
  rail: { gutterOfEnds: '8px' },
});
const confirmDialog = useConfirmDialog();
const _this = getCurrentInstance()!.proxy as any;
const modal = computed(() => _this.$store.getters.getModal);
const { translate } = useTranslate();

const cardId = computed(() => modal.value.meta?.card_id || '');
const cardTotalComments = computed(
  () => _this.$store.getters.getCardById(cardId.value).total_comments || 0
);
const owner = computed(() => _this.$store.getters.getMemberById(comment.value.user_id));

const commentId = computed(() => modal.value.resource_id);
const commentParentId = computed(() => modal.value.meta?.parent_id || '');
const commentType = computed(() => modal.value.resource_type || '');
const comment = computed(() => {
  if (commentParentId.value) {
    const parentComment = _this.$store.getters.getCommentById(commentParentId.value, cardId.value);
    if ((((parentComment || {}).children || {}).child_comments || []).length) {
      const comment = parentComment.children.child_comments.find(
        (c: any) => c.id === commentId.value
      );
      return comment || {};
    }
    return parentComment;
  }
  return _this.$store.getters.getCommentById(commentId.value, cardId.value);
});

onMounted(() => {
  confirmDialog.reveal();
});

const deleteComment = () => {
  if (_this.$route.name !== 'page' && !comment.value?.parent_id) {
    _this.$store.dispatch('updateCardTotalCommentsCount', {
      cardId: cardId.value,
      currentCommentsCount: cardTotalComments.value,
      decrease: true,
    });
  }
  // modal will close before we delete comment so we need to
  // make deep copy of the object to avoid mutation of props when it does close
  const payload = cloneDeep({
    commentId: commentId.value,
    commentType: commentType.value,
    ...(commentType.value === ResourceType.CardComment && { cardId: cardId.value }),
    parentCommentId: commentParentId.value,
  });

  hidePopUp();

  return _this.$store
    .dispatch('deleteComment', payload)
    .then(() => {
      if (_this.$route.name === 'page') {
        EventBus.$emit('removePageComment', payload.commentId);
        _this.$store.dispatch('setActiveComment', '');
      }
    })
    .catch((/* error */) => {
      if (_this.$route.name !== 'page') {
        _this.$store.dispatch('updateCardTotalCommentsCount', {
          cardId: payload.cardId,
          currentCommentsCount: cardTotalComments.value,
        });
      }
      _this.$store.dispatch('setPopUpMessage', _this.translate('couldntDeleteComment'));
    });
};

const hidePopUp = () => {
  _this.$store.dispatch('setModal', {});
};
</script>

<style lang="scss">
.delete-comment-popup {
  @apply text-text-default;

  .comment-to-delete-preview {
    display: flex;
    flex-direction: column;
    border: 1px solid #dadcdf;
    border-radius: 8px;
    padding-left: $commentDeletePreviewPadding;
    padding-top: $commentDeletePreviewPadding;
    @apply mt-0 mb-10;
    .internal-link {
      display: inline-flex;
      max-width: -webkit-fill-available;
      max-width: -moz-available;
    }
    .posted-user-comment {
      margin-top: 3px;
      width: calc(100% - $commentDeletePreviewMarginLeftRight - $commentDeletePreviewPadding);
    }
  }
  .description-row {
    pointer-events: none;
  }
  .popup-body-message {
    font-size: 14px;
    line-height: 19px;
    font-weight: 500;
    letter-spacing: 0.2px;
    @apply pt-0 pb-4;
  }
  .delete-comment-popup-body {
    max-height: 465px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }
}

.delete-comment-popup-body::-webkit-scrollbar {
  width: 6px !important;
}
</style>
